"use client";

import { CheckIcon, ClipboardIcon } from "lucide-react";
import React, { useCallback, useState } from "react";

import { AgentIcon } from "../icons/AgentIcon";
import type {
  ChatMessageMetadata,
  ContentBlock,
  DisplayMessage,
  ErrorInfo,
  ToolCall,
} from "../types";
import { extractText, hasVisibleStreamingContent } from "../utils";

import type { BubbleVariant, HoverActionItem } from "./ChatBubble";
import { ChatBubble, HoverActionButton } from "./ChatBubble";
import type { CollapsibleMessageBodyProps } from "./CollapsibleMessageBody";
import { CollapsibleMessageBody } from "./CollapsibleMessageBody";
import { ContentBlocks } from "./ContentBlocks";
import { ErrorBlock } from "./blocks/ErrorBlock";
import { ToolCallsSection } from "./blocks/ToolCallsSection";
import { ToolResponseSection } from "./blocks/ToolResponseSection";

// ---------------------------------------------------------------------------
// MessageMetadata — price / latency / model shown below assistant bubbles
// ---------------------------------------------------------------------------

interface MessageMetadataProps {
  metadata: ChatMessageMetadata;
}

export const MessageMetadata = ({ metadata }: MessageMetadataProps) => {
  const parts: string[] = [];

  if (metadata.model) {
    parts.push(metadata.model);
  }
  if (metadata.latency_ms !== null) {
    parts.push(`${(metadata.latency_ms / 1000).toFixed(1)}s`);
  }
  if (metadata.price !== null) {
    parts.push(`$${metadata.price.toFixed(4)}`);
  }
  if (metadata.tokens) {
    const { prompt_tokens, completion_tokens } = metadata.tokens;
    if (prompt_tokens !== null && completion_tokens !== null) {
      parts.push(`${prompt_tokens}↑ ${completion_tokens}↓ tokens`);
    }
  }

  if (parts.length === 0) {
    return null;
  }

  return (
    <div className="mt-1.5 flex flex-wrap gap-x-2 gap-y-0.5 border-t border-zinc-200/60 pt-1.5">
      {parts.map((part, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={i} className="text-[10px] text-zinc-400">
          {part}
        </span>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// CopyButton — icon button used in the hover-action strip
// ---------------------------------------------------------------------------

interface CopyButtonProps {
  text: string;
}

const CopyButton = ({ text }: CopyButtonProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [text]);

  return (
    <HoverActionButton onClick={handleCopy} aria-label="Copy message">
      {copied ? (
        <CheckIcon className="h-3.5 w-3.5 text-green-500" />
      ) : (
        <ClipboardIcon className="h-3.5 w-3.5" />
      )}
    </HoverActionButton>
  );
};

// ---------------------------------------------------------------------------
// AssistantAvatar — default avatar for assistant messages
// ---------------------------------------------------------------------------

const AssistantAvatar = ({ customAgentIcon }: { customAgentIcon?: React.ReactNode }) => (
  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200">
    {customAgentIcon ?? <AgentIcon className="h-4 w-4 text-gray-600" />}
  </div>
);

// ---------------------------------------------------------------------------
// MessageProps
//
// Core props cover the universal message shape (role, content, tool_calls,
// metadata, streaming, error, etc.).
//
// Slot props let each consumer inject app-specific UI without forking the
// component:
//
//   - avatar:            Override the default assistant avatar.
//   - renderStreaming:    Replace the default streaming body (e.g. MobX
//                         observer that reads a store).
//   - renderUserContent:  Replace the default user content renderer (e.g.
//                         multimodal blocks with media previews).
//   - renderEditMode:    When provided AND isEditing is true, replaces the
//                         entire bubble body with an edit form.
//   - extraHoverActions: Additional hover-action items prepended before the
//                         default copy button.
//   - bottomMetadata:    Override the default metadata display below the
//                         bubble (e.g. latency + cost + request link).
//   - streamingSkeleton: Custom skeleton shown while waiting for the first
//                         streaming token.
//   - bubbleClassName:   Override bubble colour (e.g. MCP approval response).
//   - variant:           Bubble visual variant (default | error | editing).
//   - maxWidthClass:     Override max-width (default "max-w-[85%]").
//   - timestamp:         Formatted timestamp revealed on hover.
//   - collapsibleBody:   Enable native long-message expansion inside the bubble.
//   - hideCopyButton:    Suppress the default copy hover action.
//   - disableAnimation:  Skip the mount animation.
// ---------------------------------------------------------------------------

export interface MessageProps {
  // ---- Core message data ----
  id?: string;
  role: "user" | "assistant" | "tool";
  content: ContentBlock[];
  toolCalls?: ToolCall[];
  name?: string;
  toolCallId?: string;
  isStreaming?: boolean;
  metadata?: ChatMessageMetadata;
  error?: ErrorInfo | string | null;
  /**
   * All messages in the conversation. When provided, enables cross-message
   * approval resolution for MCP call and approval request blocks.
   */
  allMessages?: DisplayMessage[];

  // ---- Slot: avatar ----
  /** Override the default assistant avatar. Pass `null` to hide it. */
  avatar?: React.ReactNode | null;
  /** Replace the default assistant icon inside the standard avatar shell. */
  customAgentIcon?: React.ReactNode;

  // ---- Slot: streaming body ----
  /**
   * Replace the entire streaming body. Useful when the streaming content
   * lives in a MobX store and needs an observer wrapper.
   *
   * When omitted and `isStreaming` is true, the component renders
   * `streamingSkeleton` (if provided) or falls through to ContentBlocks.
   */
  renderStreaming?: () => React.ReactNode;

  // ---- Slot: user content ----
  /**
   * Replace the default user content renderer. The default renders the
   * text content as a `<span className="whitespace-pre-wrap">`.
   *
   * Useful for multimodal user messages with media blocks, variable badges, etc.
   */
  renderUserContent?: () => React.ReactNode;

  // ---- Slot: edit mode ----
  /**
   * When `isEditing` is true AND this render prop is provided, the entire
   * bubble body is replaced with the returned node.
   */
  isEditing?: boolean;
  renderEditMode?: () => React.ReactNode;

  // ---- Slot: streaming skeleton ----
  /**
   * Custom skeleton shown while streaming hasn't produced content yet.
   * Only used when `renderStreaming` is NOT provided. When omitted, the
   * component falls through to `ContentBlocks` even during streaming.
   */
  streamingSkeleton?: React.ReactNode;

  // ---- Slot: hover actions ----
  /**
   * Additional hover-action items. Prepended before the default copy button.
   * Pass the full array including copy if you want full control.
   */
  extraHoverActions?: HoverActionItem[];

  /** Suppress the default copy hover action. */
  hideCopyButton?: boolean;

  /** Native long-message expansion inside the bubble. */
  collapsibleBody?: Omit<CollapsibleMessageBodyProps, "children">;

  // ---- Slot: bottom metadata ----
  /**
   * Override the bottom metadata below the bubble.
   * When omitted, the component renders `MessageMetadata` for assistant
   * messages that have metadata and are not streaming.
   */
  bottomMetadata?: React.ReactNode;

  // ---- Visual overrides ----
  /** Override bubble colour classes (e.g. MCP approval response tinting). */
  bubbleClassName?: string;
  /** Override the automatic role-based alignment. */
  align?: "left" | "right";
  /** Bubble visual variant. */
  variant?: BubbleVariant;
  /** Max-width class for the bubble. Defaults to "max-w-[85%]". */
  maxWidthClass?: string;
  /** Formatted timestamp revealed on hover above the bubble. */
  timestamp?: string;

  // ---- Animation ----
  /** Unique key for AnimatePresence. Pass `message.id` or similar. */
  animateKey?: string;
  /** Skip the mount animation. */
  disableAnimation?: boolean;

  // ---- Misc ----
  className?: string;
}

export const Message = React.memo(
  ({
    id,
    role,
    content,
    toolCalls,
    name,
    toolCallId,
    isStreaming,
    metadata,
    error,
    allMessages,
    avatar,
    customAgentIcon,
    renderStreaming,
    renderUserContent,
    isEditing,
    renderEditMode,
    streamingSkeleton,
    extraHoverActions,
    hideCopyButton,
    collapsibleBody,
    bottomMetadata,
    bubbleClassName,
    align,
    variant = "default",
    maxWidthClass = "max-w-[85%]",
    timestamp,
    animateKey,
    disableAnimation,
    className,
  }: MessageProps) => {
    const isUser = role === "user";
    const isTool = role === "tool";
    const alignRight = align ? align === "right" : undefined;
    const isRightAligned = alignRight ?? (isUser || isTool);

    const safeContent = content ?? [];
    const plainText = extractText(safeContent);
    const hasContent = safeContent.length > 0;

    // Tool response messages with no content have nothing meaningful to show.
    if (isTool && !hasContent) {
      return null;
    }

    // ---- Check if all streaming content blocks are empty ----
    const allContentEmpty =
      isStreaming && !hasVisibleStreamingContent(safeContent);

    // ---- Hover actions ----
    const defaultCopyAction: HoverActionItem[] =
      plainText && !isStreaming && !hideCopyButton
        ? [{ key: "copy", node: <CopyButton text={plainText} /> }]
        : [];

    const hoverActions =
      isEditing && renderEditMode
        ? undefined
        : [...(extraHoverActions ?? []), ...defaultCopyAction];

    const effectiveHoverActions =
      hoverActions && hoverActions.length > 0 ? hoverActions : undefined;

    // ---- Bottom metadata ----
    // If the consumer provides explicit bottomMetadata (including `null`), use it.
    // Otherwise fall back to the default MessageMetadata for assistant messages.
    /* eslint-disable no-nested-ternary */
    const resolvedBottomMetadata =
      bottomMetadata !== undefined ? (
        bottomMetadata
      ) : !isRightAligned && metadata && !isStreaming ? (
        <MessageMetadata metadata={metadata} />
      ) : undefined;
    /* eslint-enable no-nested-ternary */

    // ---- Avatar ----
    // `avatar` prop: ReactNode = override, null = hide, undefined = default.
    /* eslint-disable no-nested-ternary */
    const resolvedAvatar =
      avatar !== undefined ? (
        avatar
      ) : !isRightAligned ? (
        <AssistantAvatar customAgentIcon={customAgentIcon} />
      ) : undefined;
    /* eslint-enable no-nested-ternary */

    // ---- Render body ----
    let body: React.ReactNode;

    if (isEditing && renderEditMode) {
      // ---- Edit mode: fully replaced by consumer ----
      body = renderEditMode();
    } else if (isTool) {
      // ---- Tool response: always rendered with ToolResponseSection ----
      body = (
        <ToolResponseSection
          message={{
            id: id ?? "",
            role: "tool",
            content: safeContent,
            name,
            tool_call_id: toolCallId,
          }}
        />
      );
    } else if (isUser) {
      // ---- User message: delegate to consumer or default ----
      if (renderUserContent) {
        body = renderUserContent();
      } else {
        const nonTextBlocks = safeContent.filter((b) => b.type !== "text");
        body = (
          <>
            {plainText && (
              <span className="whitespace-pre-wrap">{plainText}</span>
            )}
            {nonTextBlocks.length > 0 && (
              <ContentBlocks content={nonTextBlocks} />
            )}
          </>
        );
      }
    } else if (isStreaming && renderStreaming) {
      // ---- Streaming with custom renderer (e.g. MobX observer) ----
      body = renderStreaming();
    } else {
      // ---- Assistant (or streaming fallback) ----
      body = (
        <>
          {/* Streaming skeleton — shown while waiting for first token */}
          {isStreaming && allContentEmpty && streamingSkeleton}

          {/* Content blocks */}
          {(!isStreaming || !allContentEmpty) && hasContent && (
            <ContentBlocks
              content={safeContent}
              isStreaming={isStreaming}
              allMessages={allMessages}
              error={
                // eslint-disable-next-line no-nested-ternary
                error && !isStreaming
                  ? typeof error === "string"
                    ? { message: error }
                    : error
                  : undefined
              }
            />
          )}

          {/* Standalone error (when no content to pair it with) */}
          {error && !isStreaming && !hasContent && <ErrorBlock error={error} />}

          {/* Tool calls (OpenAI function-calling style) */}
          {toolCalls && toolCalls.length > 0 && (
            <ToolCallsSection toolCalls={toolCalls} isStreaming={isStreaming} />
          )}
        </>
      );
    }

    const resolvedBody = collapsibleBody ? (
      <CollapsibleMessageBody {...collapsibleBody}>{body}</CollapsibleMessageBody>
    ) : (
      body
    );

    return (
      <ChatBubble
        role={role}
        alignRight={alignRight}
        variant={variant}
        bubbleClassName={bubbleClassName}
        animateKey={animateKey}
        disableAnimation={disableAnimation}
        timestamp={timestamp}
        avatar={resolvedAvatar}
        hoverActions={effectiveHoverActions}
        bottomMetadata={resolvedBottomMetadata}
        maxWidthClass={maxWidthClass}
        className={className}
      >
        {resolvedBody}
      </ChatBubble>
    );
  },
);
